import {
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  PlaylistAdd as QueueIcon,
  Send as SendIcon,
  StopCircle as StopIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { stripSendablePrefixes } from "../../lib/argus/argus-text";
import { RADIUS_MD } from "../../theme/theme";

export interface ArgusMessageInputProps {
  disabled?: boolean;
  placeholder?: string;
  onSend: (message: string, method?: "steer" | "followUp") => void | Promise<void>;
  /** When true, Enter sends with followUp method (pi queues it). */
  isAgentWorking?: boolean;
  /** True after user clicked Stop, until the next turn_start. */
  wasStopped?: boolean;
  /** Backend-managed queue state from queue_update SSE events. */
  queuedMessages?: { steering: readonly string[]; followUp: readonly string[] };
  onRemoveFromQueue?: (type: "steering" | "followUp", index: number) => void | Promise<void>;
  onClearQueue?: () => void | Promise<void>;
  /**
   * Push a queued message as an immediate steer. The handler is responsible
   * for removing the original from the queue and re-sending as `"steer"`.
   */
  onPromoteToSteer?: (
    type: "steering" | "followUp",
    index: number,
    text: string,
  ) => void | Promise<void>;
  /** Gracefully stop the running agent turn. */
  onStop?: () => void | Promise<void>;
  /**
   * Current context fill (latest assistant `usage.input`) and the model's
   * max context window. Drives the right-aligned progress bar below the
   * input. Omit either to hide the bar — graceful for pre-feature
   * sessions or live sessions before the first turn completes.
   */
  contextTokens?: number | null;
  contextWindow?: number | null;
}

/** Detect Mac-ish so the hint renders the correct modifier symbol. */
function useIsMac(): boolean {
  return useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const platform = navigator.platform ?? "";
    const ua = navigator.userAgent ?? "";
    return /Mac|iPhone|iPad|iPod/i.test(platform) || /Macintosh/.test(ua);
  }, []);
}

/**
 * Chat input for Argus.
 *
 * **Idle mode** (agent not working):
 *   - Enter → send
 *   - Shift+Enter / Cmd+Enter → newline
 *
 * **Working mode** (agent mid-turn):
 *   - Enter → send with followUp (pi queues it natively)
 *   - Shift+Enter / Cmd+Enter → newline
 *   - Stop button → abort current turn
 *
 * Queue state comes from pi-coding-agent's `queue_update` SSE events —
 * the frontend is a view of the backend queue, not local state.
 */
export const ArgusMessageInput = ({
  disabled = false,
  placeholder = "Ask Argus…",
  onSend,
  isAgentWorking = false,
  wasStopped = false,
  queuedMessages,
  onRemoveFromQueue,
  onClearQueue,
  onPromoteToSteer,
  onStop,
  contextTokens,
  contextWindow,
}: ArgusMessageInputProps) => {
  const [value, setValue] = useState("");
  const isMac = useIsMac();
  const hasText = value.trim().length > 0;

  // Merge both queues into a single flat list for display.
  const allQueued = useMemo(() => {
    const items: {
      text: string;
      type: "steering" | "followUp";
      index: number;
    }[] = [];
    for (const [i, msg] of (queuedMessages?.steering ?? []).entries()) {
      items.push({
        text: stripSendablePrefixes(msg),
        type: "steering",
        index: i,
      });
    }
    for (const [i, msg] of (queuedMessages?.followUp ?? []).entries()) {
      items.push({
        text: stripSendablePrefixes(msg),
        type: "followUp",
        index: i,
      });
    }
    return items;
  }, [queuedMessages]);

  const handleSend = async (method?: "steer" | "followUp") => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    setValue("");
    await onSend(trimmed, method);
  };

  const handleStop = async () => {
    if (onStop) await onStop();
  };

  const effectivePlaceholder = isAgentWorking ? "Send a follow-up…" : placeholder;
  const newlineHint = isMac ? "⌘↵" : "Ctrl+↵";

  return (
    <Box
      sx={(theme) => ({
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        // Tighter horizontal padding on small viewports so the textarea
        // gets the most width on phones. Desktop keeps the 16px hug.
        paddingX: { xs: 1, md: 2 },
        paddingY: 1.5,
        "&::before": {
          content: '""',
          position: "absolute",
          bottom: "100%",
          left: 0,
          right: 0,
          height: 24,
          background: `linear-gradient(to top, ${theme.palette.background.default}, transparent)`,
          pointerEvents: "none",
        },
      })}
    >
      {/* Stopped indicator */}
      <Collapse in={wasStopped && !isAgentWorking}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            paddingX: 1.5,
            paddingY: 0.75,
            marginBottom: 0.5,
            borderRadius: 1,
            backgroundColor: "warning.main",
            color: "warning.contrastText",
            fontSize: "0.75rem",
            fontWeight: 500,
          }}
        >
          <StopIcon sx={{ fontSize: 14 }} />
          Session stopped — send a message to continue
        </Box>
      </Collapse>

      {/* Queue panel — rendered above input, merged visually */}
      <Collapse in={allQueued.length > 0}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderBottom: "none",
            borderTopLeftRadius: `${RADIUS_MD}px`,
            borderTopRightRadius: `${RADIUS_MD}px`,
            backgroundColor: "action.hover",
            paddingX: 1.5,
            paddingY: 1,
            maxHeight: 150,
            overflow: "auto",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ marginBottom: allQueued.length > 0 ? 0.5 : 0 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                fontSize: "0.7rem",
              }}
            >
              {allQueued.length} Queued
            </Typography>
            {allQueued.length > 0 && onClearQueue && (
              <Typography
                component="span"
                variant="caption"
                onClick={() => void onClearQueue()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") void onClearQueue();
                }}
                sx={{
                  cursor: "pointer",
                  color: "text.disabled",
                  fontSize: "0.65rem",
                  "&:hover": {
                    color: "error.main",
                    textDecoration: "underline",
                  },
                }}
              >
                Clear All
              </Typography>
            )}
          </Stack>
          <Stack spacing={0.25}>
            {allQueued.map((item) => (
              <QueueItem
                key={`${item.type}-${item.index}-${item.text.slice(0, 12)}`}
                label={item.text}
                onRemove={
                  onRemoveFromQueue
                    ? () => void onRemoveFromQueue(item.type, item.index)
                    : undefined
                }
                onPromote={
                  onPromoteToSteer
                    ? () => void onPromoteToSteer(item.type, item.index, item.text)
                    : undefined
                }
              />
            ))}
          </Stack>
        </Box>
      </Collapse>

      {/* Single bordered container holding the textarea (top) and a footer
          toolbar (bottom) with hint on the left + context indicator and
          send/stop on the right. Matches the Cursor/Claude-style input
          pattern Taras asked for: 2-line default, actions inline. */}
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderColor: "divider",
          borderRadius:
            allQueued.length > 0 ? `0 0 ${RADIUS_MD}px ${RADIUS_MD}px` : `${RADIUS_MD}px`,
          backgroundColor: theme.palette.background.paper,
          paddingX: 1.5,
          paddingY: 1,
          transition: "border-color 120ms",
          "&:focus-within": { borderColor: "primary.main" },
        })}
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
          variant="standard"
          value={value}
          disabled={disabled}
          placeholder={effectivePlaceholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            if (e.shiftKey || e.metaKey || e.ctrlKey) return;
            if (e.nativeEvent.isComposing) return;
            e.preventDefault();
            // When agent is working, send as followUp (pi queues it natively).
            void handleSend(isAgentWorking ? "followUp" : undefined);
          }}
          slotProps={{ input: { disableUnderline: true } }}
          sx={{ "& textarea": { fontSize: "0.9rem" } }}
        />

        {/* Bottom toolbar: hint (left) · indicator + stop + send (right). */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            marginTop: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled",
              fontSize: "0.7rem",
              userSelect: "none",
            }}
          >
            <Box component="kbd" sx={{ fontFamily: "inherit", fontWeight: 600 }}>
              ↵
            </Box>{" "}
            to {isAgentWorking ? "follow-up" : "send"} ·{" "}
            <Box component="kbd" sx={{ fontFamily: "inherit", fontWeight: 600 }}>
              {newlineHint}
            </Box>{" "}
            for newline
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ContextUsageIndicator contextTokens={contextTokens} contextWindow={contextWindow} />
            {isAgentWorking && (
              <Tooltip title="Stop Argus" arrow>
                <IconButton
                  onClick={() => void handleStop()}
                  disabled={disabled}
                  aria-label="Stop Argus"
                  size="small"
                  sx={{
                    color: "error.main",
                    "&:hover": {
                      backgroundColor: "error.main",
                      color: "error.contrastText",
                    },
                  }}
                >
                  <StopIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isAgentWorking ? "Queue Follow-up" : "Send Message"} arrow>
              <span>
                <IconButton
                  onClick={() => void handleSend(isAgentWorking ? "followUp" : undefined)}
                  disabled={disabled || !hasText}
                  aria-label={isAgentWorking ? "Queue Follow-up" : "Send Message"}
                  color="primary"
                  size="small"
                >
                  {isAgentWorking ? <QueueIcon fontSize="small" /> : <SendIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

/** Format token counts compactly: 30_000 → "30k", 1_000_000 → "1m". */
const formatTokensCompact = (n: number): string => {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m >= 10 ? m.toFixed(0) : m.toFixed(1).replace(/\.0$/, "")}m`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    return `${k >= 10 ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, "")}k`;
  }
  return String(n);
};

/**
 * Small circular context-fill indicator with the percentage inside.
 * Colour thresholds:
 *   - green (success)  <60%
 *   - yellow (warning) <85%
 *   - red    (error)   ≥85% — lines up with pi-coding-agent's default
 *     auto-compaction threshold (~84%) so users get a visual heads-up
 *     right before the agent summarises earlier history.
 *
 * Renders nothing when either value is missing — pre-feature sessions
 * and live sessions before the first `message_end` have no signal yet,
 * and a half-populated indicator would be noisier than nothing.
 */
const ContextUsageIndicator = ({
  contextTokens,
  contextWindow,
}: {
  contextTokens?: number | null;
  contextWindow?: number | null;
}) => {
  if (contextTokens == null || contextWindow == null || contextWindow <= 0 || contextTokens < 0) {
    return null;
  }
  const rawPct = (contextTokens / contextWindow) * 100;
  const pct = Math.min(100, Math.max(0, rawPct));
  const color: "success" | "warning" | "error" =
    pct >= 85 ? "error" : pct >= 60 ? "warning" : "success";
  const display = pct >= 1 ? `${pct.toFixed(0)}` : "<1";
  const size = 28;
  const thickness = 4;
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ textAlign: "center" }}>
          <Typography
            component="div"
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Context Window
          </Typography>
          <Typography component="div" variant="body2">
            {pct.toFixed(0)}% · {formatTokensCompact(contextTokens)}/
            {formatTokensCompact(contextWindow)} context used
          </Typography>
        </Box>
      }
    >
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          cursor: "default",
        }}
      >
        {/* Track circle — subtle full-circle backdrop */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={thickness}
          sx={{
            color: "action.disabledBackground",
            position: "absolute",
            inset: 0,
          }}
        />
        <CircularProgress
          variant="determinate"
          value={pct}
          size={size}
          thickness={thickness}
          color={color}
          sx={{ position: "absolute", inset: 0 }}
        />
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            fontSize: "0.6rem",
            fontWeight: 600,
            lineHeight: 1,
            color: "text.secondary",
            userSelect: "none",
          }}
        >
          {display}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Queue item — compact row with hover-reveal delete
// ---------------------------------------------------------------------------

const QueueItem = ({
  label,
  onRemove,
  onPromote,
}: {
  label: string;
  onRemove?: () => void;
  onPromote?: () => void;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      paddingX: 1,
      paddingY: 0.25,
      borderRadius: 0.5,
      "&:hover": { backgroundColor: "action.selected" },
      "&:hover .queue-item-action": { opacity: 1 },
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        flexShrink: 0,
        border: "1.5px solid",
        borderColor: "text.disabled",
      }}
    />
    <Typography
      variant="caption"
      sx={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: "text.secondary",
        fontSize: "0.75rem",
      }}
    >
      {label}
    </Typography>
    {onPromote && (
      <Tooltip title="Send now (steer)" arrow>
        <IconButton
          size="small"
          className="queue-item-action"
          onClick={onPromote}
          aria-label="Send now"
          sx={{
            opacity: 0,
            transition: "opacity 0.15s",
            padding: 0.25,
            color: "text.disabled",
            "&:hover": { color: "primary.main" },
          }}
        >
          <PlayArrowIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    )}
    {onRemove && (
      <IconButton
        size="small"
        className="queue-item-action"
        onClick={onRemove}
        aria-label="Remove from queue"
        sx={{
          opacity: 0,
          transition: "opacity 0.15s",
          padding: 0.25,
          color: "text.disabled",
          "&:hover": { color: "error.main" },
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    )}
  </Box>
);
